import React, { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import "../../../assets/custom css/loadoverlay.scss";
import DriverLocationService from "./driverLocation.service";
import AuthService from "../../Authentication/auth.service";
import AlertService from "../../../components/Alerts/alerts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faCar, faMapMarkerAlt, faUser, faBox } from "@fortawesome/free-solid-svg-icons";
import Map, { Marker, Popup, NavigationControl, Source, Layer, FullscreenControl } from 'react-map-gl';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapboxConfig } from "../../../mapboxConfig";
import axios from "axios";
import "./mapbox-styles.css";

// Set global token for Mapbox
mapboxgl.accessToken = mapboxConfig.publicToken;

// Fixing WebWorker bundle parsing error in production
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const DriverLocationsMap = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [driversLocations, setDriversLocations] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [viewState, setViewState] = useState({
    latitude: 3.1390,
    longitude: 101.6869,
    zoom: 10
  });
  const [popupInfo, setPopupInfo] = useState(null);
  const [routes, setRoutes] = useState([]);
  const mapRef = useRef(null);

  // Track selected order for each driver: { driverId: orderIndex }
  const [selectedOrderForDriver, setSelectedOrderForDriver] = useState({});

  // Function to find all orders for a customer from active drivers
  const findCustomerOrders = (customerId, locationsData) => {
    const orders = [];
    const customerIdStr = customerId ? customerId.toString() : null;
    if (!customerIdStr) return orders;

    const locations = locationsData || driversLocations;
    if (!locations || locations.length === 0) return orders;

    locations.forEach(driverData => {
      if (driverData.orders && driverData.orders.length > 0) {
        driverData.orders.forEach(order => {
          if (order.customer_id) {
            const orderCustomerId = typeof order.customer_id === 'object' && order.customer_id.toString
              ? order.customer_id.toString()
              : order.customer_id.toString();

            if (orderCustomerId === customerIdStr) {
              orders.push({
                ...order,
                driver_name: driverData.driver_name,
                driver_id: driverData.driver_id
              });
            }
          }
        });
      }
    });
    return orders;
  };

  // Generate unique color for each driver based on their ID
  const getDriverColor = (driverId) => {
    const colors = [
      '#1E3A8A', '#6B21A8', '#7C3AED', '#4338CA', '#1E40AF',
      '#7E22CE', '#DC2626', '#B91C1C', '#EA580C', '#C2410C',
      '#059669', '#047857', '#0D9488', '#BE185D', '#9F1239',
      '#831843', '#78350F', '#92400E', '#1F2937', '#374151'
    ];

    const hash = driverId.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);

    const step = 5;
    const colorIndex = (Math.abs(hash) * step) % colors.length;
    return colors[colorIndex];
  };

  // Fetch driver locations
  const fetchDriverLocations = useCallback((isRefresh = false) => {
    if (isInitialLoad && !isRefresh) {
      setLoading(true);
    }

    DriverLocationService.getActiveDriversLocations({})
      .then((response) => {
        if (isInitialLoad) {
          setLoading(false);
          setIsInitialLoad(false);
        }

        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else if (response.data.status) {
          const locationsData = response.data.data || [];
          const allCustomersData = response.data.all_customers || [];

          setDriversLocations(locationsData);
          setAllCustomers(allCustomersData);

          // Initial map fit bounds logic could go here
          if (locationsData.length > 0 && isInitialLoad && !isRefresh) {
            const firstDriver = locationsData[0];
            if (firstDriver.location && firstDriver.location.latitude && firstDriver.location.longitude) {
              setViewState(prev => ({
                ...prev,
                latitude: firstDriver.location.latitude,
                longitude: firstDriver.location.longitude,
                zoom: 10
              }));
            }
          }

          // Generate routes
          generateRoutes(locationsData);

        } else {
          if (isInitialLoad) {
            AlertService.swalErrorAlert(response.data.message || t("Failed to fetch driver locations."));
          }
        }
      })
      .catch((error) => {
        if (isInitialLoad) {
          setLoading(false);
          setIsInitialLoad(false);
        }

        if (isInitialLoad) {
          const errContent = (error.response && error.response.data) || error.message || error.toString();
          AlertService.swalErrorAlert(errContent || t("Server Error, Please try again later."));
        }
      });
  }, [isInitialLoad, navigate, t]);

  // Extract coordinates from Google Maps URL
  const extractCoordinatesFromGoogleUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;

      // Method 1: !2m2!1d...!2d...
      const all2m2Matches = [...url.matchAll(/!2m2!1d(-?\d+\.?\d*)!2d(-?\d+\.?\d*)/g)];
      if (all2m2Matches.length > 0) {
        const lastMatch = all2m2Matches[all2m2Matches.length - 1];
        return { lng: parseFloat(lastMatch[1]), lat: parseFloat(lastMatch[2]) };
      }

      // Method 2: !3d...!4d...
      const allD3d4Matches = [...url.matchAll(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/g)];
      if (allD3d4Matches.length > 0) {
        const lastMatch = allD3d4Matches[allD3d4Matches.length - 1];
        return { lat: parseFloat(lastMatch[1]), lng: parseFloat(lastMatch[2]) };
      }

      // Method 3: @lat,lng
      const allAtMatches = [...url.matchAll(/@(-?\d+\.?\d*),(-?\d+\.?\d*)(?:[,\/]|$)/g)];
      if (allAtMatches.length > 0) {
        const lastMatch = allAtMatches[allAtMatches.length - 1];
        return { lat: parseFloat(lastMatch[1]), lng: parseFloat(lastMatch[2]) };
      }

      // Method 4: q=lat,lng
      const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (qMatch && qMatch.length >= 3) {
        return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
      }

      return null;
    } catch (error) {
      console.error("Error extracting coordinates:", error);
      return null;
    }
  };

  // Geocode address using Mapbox
  const geocodeAddress = async (address) => {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxConfig.publicToken}&limit=1`
      );
      if (response.data.features && response.data.features.length > 0) {
        const [lng, lat] = response.data.features[0].center;
        return { lat, lng };
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    }
    return null;
  };

  // Get coordinates helper
  const getCoordinatesFromLocation = async (locationData) => {
    if (!locationData) return null;

    // 1. Direct coordinates
    if (locationData.latitude && locationData.longitude) {
      const lat = parseFloat(locationData.latitude);
      const lng = parseFloat(locationData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }

    // 2. Google Loc URL
    if (locationData.google_loc) {
      const coords = extractCoordinatesFromGoogleUrl(locationData.google_loc);
      if (coords) return coords;

      // Extract address from daddr
      const daddrMatch = locationData.google_loc.match(/[?&]daddr=([^&]+)/);
      if (daddrMatch && daddrMatch.length >= 2) {
        const address = decodeURIComponent(daddrMatch[1].replace(/\+/g, ' '));
        return await geocodeAddress(address);
      }
    }

    // 3. Address field
    if (locationData.address) {
      return await geocodeAddress(locationData.address);
    }

    return null;
  };

  // Generate routes for drivers
  const generateRoutes = async (locations) => {
    if (!locations || locations.length === 0) {
      console.log("No drivers to generate routes for.");
      setRoutes([]);
      return;
    }

    const newRoutes = [];

    for (const driver of locations) {
      if (!driver.location || !driver.location.latitude || !driver.location.longitude) continue;

      // Get destination
      let destinationCoords = null;

      // Try driver's assigned destination first
      if (driver.destination) {
        destinationCoords = await getCoordinatesFromLocation(driver.destination);
      }

      // If not, try first order's customer
      if (!destinationCoords && driver.orders && driver.orders.length > 0) {
        const selectedIndex = selectedOrderForDriver[driver.driver_id] || 0;
        const order = driver.orders[selectedIndex];

        if (order && order.customer_id) {
          const customer = allCustomers.find(c => (c._id || c.id) === order.customer_id.toString());
          if (customer) {
            destinationCoords = await getCoordinatesFromLocation(customer);
          }
        }
      }

      if (destinationCoords && destinationCoords.lat && destinationCoords.lng) {
        // Fetch route from Mapbox
        const startLng = parseFloat(driver.location.longitude);
        const startLat = parseFloat(driver.location.latitude);
        const endLng = parseFloat(destinationCoords.lng);
        const endLat = parseFloat(destinationCoords.lat);

        // Final sanity check before API call
        if (isNaN(startLng) || isNaN(startLat) || isNaN(endLng) || isNaN(endLat)) {
          console.warn(`Invalid coordinates for driver ${driver.driver_id}`, { startLng, startLat, endLng, endLat });
          continue;
        }

        const start = `${startLng},${startLat}`;
        const end = `${endLng},${endLat}`;

        try {
          const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${start};${end}?steps=true&geometries=geojson&access_token=${mapboxConfig.publicToken}`;

          const query = await axios.get(directionsUrl);

          if (query.data && query.data.routes && query.data.routes[0]) {
            const route = query.data.routes[0];
            const driverColor = getDriverColor(driver.driver_id);

            newRoutes.push({
              id: `route-${driver.driver_id}`,
              driverId: driver.driver_id,
              geometry: route.geometry,
              color: driverColor,
              distance: (route.distance / 1000).toFixed(2) + ' km',
              duration: (route.duration / 60).toFixed(0) + ' min',
              destinationCoords: destinationCoords,
              destinationName: driver.destination?.customer_name || "Customer"
            });
          }
        } catch (err) {
          console.error(`Route fetch failed for driver ${driver.driver_id}:`, err.message);
        }
      }
    }
    setRoutes(newRoutes);
  };

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDriverLocations(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchDriverLocations]);

  // Initial fetch
  useEffect(() => {
    console.log("DriverLocationsMap: component mounted");
    fetchDriverLocations(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">

                {/* Header */}
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-map-marker icon-gradient bg-happy-green" />
                      </div>
                      <div>
                        {t('Driver Locations')}
                        <div className="page-title-subheading">
                          {t('Real-time tracking of active drivers')}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-hover-shine me-3"
                        onClick={() => fetchDriverLocations(false)}
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faSync} className="me-2" spin={loading} />
                        {t('Refresh')}
                      </button>
                    </div>
                  </div>
                </div>

                <Row>
                  <Col md="12">
                    <Card className="main-card mb-3">
                      <CardBody style={{ position: 'relative', padding: 0, height: '600px' }}>
                        <LoadingOverlay
                          tag="div"
                          active={loading && isInitialLoad}
                          styles={{ overlay: (base) => ({ ...base }) }}
                          spinner={<DNALoader />}
                        >
                          <Map
                            {...viewState}
                            onMove={evt => setViewState(evt.viewState)}
                            style={{ width: '100%', height: '600px', borderRadius: '8px' }}
                            mapStyle="mapbox://styles/mapbox/streets-v11"
                            mapboxAccessToken={mapboxConfig.publicToken}
                            mapLib={mapboxgl}
                          >
                            <NavigationControl position="top-right" />
                            <FullscreenControl position="top-right" />

                            {/* Render Routes */}
                            {routes.map(route => (
                              <Source key={route.id} type="geojson" data={{
                                type: 'Feature',
                                properties: {},
                                geometry: route.geometry
                              }}>
                                <Layer
                                  id={route.id}
                                  type="line"
                                  layout={{
                                    'line-join': 'round',
                                    'line-cap': 'round'
                                  }}
                                  paint={{
                                    'line-color': route.color,
                                    'line-width': 5,
                                    'line-opacity': 0.8
                                  }}
                                />
                              </Source>
                            ))}

                            {/* Render Destination Markers */}
                            {routes.map(route => (
                              <Marker
                                key={`dest-${route.id}`}
                                longitude={route.destinationCoords.lng}
                                latitude={route.destinationCoords.lat}
                                anchor="bottom"
                                onClick={e => {
                                  e.originalEvent.stopPropagation();
                                  setPopupInfo({
                                    type: 'destination',
                                    data: route,
                                    lat: route.destinationCoords.lat,
                                    lng: route.destinationCoords.lng
                                  });
                                }}
                              >
                                <div style={{ color: '#EA4335', fontSize: '24px' }}>
                                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                                </div>
                              </Marker>
                            ))}

                            {/* Render Driver Markers */}
                            {driversLocations.map((driver, index) => {
                              if (!driver.location || !driver.location.longitude) return null;

                              return (
                                <Marker
                                  key={`driver-${driver.driver_id}`}
                                  longitude={parseFloat(driver.location.longitude)}
                                  latitude={parseFloat(driver.location.latitude)}
                                  anchor="center"
                                  onClick={e => {
                                    e.originalEvent.stopPropagation();
                                    setPopupInfo({
                                      type: 'driver',
                                      data: driver,
                                      lat: parseFloat(driver.location.latitude),
                                      lng: parseFloat(driver.location.longitude)
                                    });
                                  }}
                                >
                                  <div style={{
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    padding: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                    color: getDriverColor(driver.driver_id)
                                  }}>
                                    <FontAwesomeIcon icon={faCar} size="lg" />
                                  </div>
                                </Marker>
                              );
                            })}

                            {/* Render Customer Markers (active orders that are not destinations) */}
                            {allCustomers.map((customer) => {
                              // Logic to hide customers that are already destinations could be complex to perfectly check coordinates
                              // simplified: show all customers as small dots?
                              // For now, let's skip customer markers primarily map clutter unless specifically requested
                              // or we can implement lazy loading. 
                              // User asked to "remove google map ... use map box". I'll replicate core function.
                              return null;
                            })}

                            {/* Popup */}
                            {popupInfo && (
                              <Popup
                                anchor="top"
                                longitude={Number(popupInfo.lng)}
                                latitude={Number(popupInfo.lat)}
                                onClose={() => setPopupInfo(null)}
                                maxWidth="300px"
                              >
                                <div className="p-2">
                                  {popupInfo.type === 'driver' && (
                                    <>
                                      <h6 className="fw-bold mb-2">{popupInfo.data.driver_name || 'Driver'}</h6>
                                      <p className="mb-1 text-muted small">Orders: {popupInfo.data.orders ? popupInfo.data.orders.length : 0}</p>
                                      <p className="mb-1 text-muted small">Speed: {popupInfo.data.location.speed ? (popupInfo.data.location.speed * 3.6).toFixed(1) + ' km/h' : '0 km/h'}</p>
                                      <hr className="my-2" />
                                      {popupInfo.data.orders && popupInfo.data.orders.map((o, i) => (
                                        <div key={i} className="mb-1 small">
                                          <FontAwesomeIcon icon={faBox} className="me-1 text-primary" />
                                          {o.order_no} - {o.customer_name || 'Customer'}
                                        </div>
                                      ))}
                                    </>
                                  )}

                                  {popupInfo.type === 'destination' && (
                                    <>
                                      <h6 className="fw-bold mb-2">Destination</h6>
                                      <p className="mb-1 small"><strong>Customer:</strong> {popupInfo.data.destinationName}</p>
                                      <p className="mb-1 small"><strong>Distance:</strong> {popupInfo.data.distance}</p>
                                      <p className="mb-1 small"><strong>Duration:</strong> {popupInfo.data.duration}</p>
                                    </>
                                  )}
                                </div>
                              </Popup>
                            )}

                          </Map>
                        </LoadingOverlay>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Driver Locations List Table */}
                {driversLocations.length > 0 && (
                  <Row>
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>{t('Driver Name')}</th>
                                  <th>{t('Location')}</th>
                                  <th>{t('Active Orders')}</th>
                                  <th>{t('Last Update')}</th>
                                  <th>{t('Actions')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {driversLocations.map((driverData, index) => (
                                  <tr key={index} style={{ cursor: 'pointer' }} onClick={() => {
                                    if (driverData.location) {
                                      setViewState({
                                        latitude: parseFloat(driverData.location.latitude),
                                        longitude: parseFloat(driverData.location.longitude),
                                        zoom: 14,
                                        transitionDuration: 1000
                                      });
                                      setPopupInfo({
                                        type: 'driver',
                                        data: driverData,
                                        lat: parseFloat(driverData.location.latitude),
                                        lng: parseFloat(driverData.location.longitude)
                                      });
                                    }
                                  }}>
                                    <td>
                                      <span
                                        style={{ color: '#4285F4', textDecoration: 'underline', fontWeight: 'bold' }}
                                      >
                                        {driverData.driver_name || driverData.driver_id.substring(0, 12) + '...'}
                                      </span>
                                    </td>
                                    <td>
                                      {driverData.location ? (
                                        <>
                                          {parseFloat(driverData.location.latitude).toFixed(6)}, {parseFloat(driverData.location.longitude).toFixed(6)}
                                        </>
                                      ) : t('N/A')}
                                    </td>
                                    <td>
                                      <span className="badge bg-primary">
                                        {driverData.orders ? driverData.orders.length : 0}
                                      </span>
                                    </td>
                                    <td>
                                      {driverData.location && driverData.location.timestamp
                                        ? new Date(driverData.location.timestamp).toLocaleString()
                                        : t('N/A')}
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn-sm btn-primary me-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/driver-tracking-history/${driverData.driver_id}`);
                                        }}
                                      >
                                        {t('View History')}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

              </Col>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DriverLocationsMap;
